import React, { FunctionComponent, useMemo, useState } from "react";
import { Grid, GridSize, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { ISellValue, useGetSellValues } from "../../../../hooks/sellValues";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifySellValueDialog } from "../ModifySellValueDialog";
import { SellValueCard } from "../SellValueCard";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import styles from "./styles.module.scss";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const SellValuesDesktop: FunctionComponent<ISellValuesDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedId,
  onSellValueSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [sellValues, resetSellValues, setSellValues, noMoreSellValues] = useGetSellValues(
    isPublic,
    setLoading
  );
  const {
    state: modifyDialogState,
    key: modifyDialogKey,
    openAdd,
    openEdit,
    close: closeModifyDialog
  } = useResettableDialog<ISellValue>();
  const [searchText, setSearchText] = useState("");
  const [contextSellValue, setContextSellValue] = useState<ISellValue>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredSellValues = useMemo(() => {
    if (searchText == "") {
      return sellValues;
    }
    return sellValues?.filter(sellValue => {
      return (
        sellValue.group.toLowerCase().includes(searchText.toLowerCase()) ||
        sellValue.dollars.toString().toLowerCase().includes(searchText.toLowerCase()) ||
        sellValue.description.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [sellValues, searchText]);
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredSellValues}
      emptyIconType={attributeIconMap[AttributeType.SELL_VALUES]}
      emptyString={isPublic ? strings.empty_sell_values_public : strings.empty_sell_values}
      addItemString={strings.add_attribute}
      title={strings.attribute_sell_values_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetSellValues}
      onClearValue={onClearValue}
    >
      {filteredSellValues?.map(sellValue => (
        <Grid item key={sellValue.uuid} xs={columnWidth}>
          <SellValueCard
            sellValue={sellValue}
            selected={preselectedId == sellValue.uuid}
            onClick={() => {
              if (onSellValueSelected) {
                onSellValueSelected(sellValue);
              } else {
                openEdit(sellValue);
              }
            }}
            onContextMenu={event => {
              setContextSellValue(sellValue);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreSellValues) &&
        Array.from({ length: 40 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 80 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextSellValue);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifySellValueDialog
        key={modifyDialogKey}
        open={modifyDialogState.mode !== "closed"}
        sellValue={modifyDialogState.item}
        onClose={closeModifyDialog}
        onNewItem={newSellValue => {
          const newSellValues = [...sellValues, newSellValue];
          newSellValues.sort((a, b) =>
            a.group.localeCompare(b.group, undefined, { ignorePunctuation: true })
          );
          setSellValues(newSellValues);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newSellValue => {
          const newSellValues = sellValues.map(sellValue =>
            sellValue.uuid == newSellValue.uuid ? newSellValue : sellValue
          );
          newSellValues.sort((a, b) =>
            a.group.localeCompare(b.group, undefined, { ignorePunctuation: true })
          );
          setSellValues(newSellValues);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedSellValue => {
          const newSellValues = sellValues.filter(
            sellValue => sellValue.uuid != deletedSellValue.uuid
          );
          setSellValues(newSellValues);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface ISellValuesDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: GridSize;
  preselectedId?: string;
  onSellValueSelected?: (sellValue: ISellValue) => void;
  onClearValue?: () => void;
}
